import type { IssueType } from '@hetivo/hetivo-lib-types';
import { ISSUE_TYPES } from '@hetivo/hetivo-lib-types';
import { z } from 'zod/v4';

// ─── Tabs ───────────────────────────────────────────────────────────────────

/** The Issues list tabs, in display order (SWOOP-255). `all` is the default. */
export const ISSUE_TABS = ['all', 'open', 'inProgress', 'resolved'] as const;

export type IssueTab = (typeof ISSUE_TABS)[number];

// ─── URL search schema ──────────────────────────────────────────────────────

const ISSUE_TYPE_VALUES = ISSUE_TYPES;

/**
 * The Issues list URL search-param schema (SWOOP-255). Every field is optional + `.catch(undefined)` so
 * a hand-edited or stale URL degrades to the defaults (applied by the consuming hook) instead of
 * throwing, and a plain navigation to `/issues` needs no search object. `issue` keeps the open detail
 * drawer's issue id in the URL so a reload or a Back restores it (US4).
 */
export const issuesListSearchSchema = z.object({
  issue: z.string().optional().catch(undefined),
  page: z.number().int().positive().optional().catch(undefined),
  search: z.string().optional().catch(undefined),
  tab: z.enum(ISSUE_TABS).optional().catch(undefined),
  type: z.enum(ISSUE_TYPE_VALUES).optional().catch(undefined),
});

export type IssuesListSearch = z.infer<typeof issuesListSearchSchema>;

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Narrows a raw string to an {@link IssueType}, or `undefined` when it is not one. */
export function toIssueType(value: string | undefined): IssueType | undefined {
  return ISSUE_TYPE_VALUES.find((type) => type === value);
}

/** Narrows a raw string to an {@link IssueTab}, falling back to `all`. */
export function toIssueTab(value: string | undefined): IssueTab {
  return ISSUE_TABS.find((tab) => tab === value) ?? 'all';
}
